import PropTypes from 'prop-types';
import React from 'react';
import NoSSR from 'react-no-ssr';
import Barchart from '../../../components/Charts/Barchart/Barchart';
import MinimalCircularChart from '../../../components/Charts/MinimalCircularChart/MinimalCircularChart';
import SliderChart from '../../../components/Charts/SliderChart/SliderChart';
import CompanyReviewSlide from '../../../components/CompanyPages/CompanyReviewSlide/CompanyReviewSlide';
import FGBLabel from '../../../components/Elements/FGBLabel/FGBLabel';
import Button from '../../../components/Input/Button/Button';
import Slider from '../../../components/JobComponents/Silder/Slider';
import Rating from '../../../components/JobV3/Elements/Rating/Rating';
import Layout from '../../../components/Layout/Layout';
import LeftRailContent from '../../../components/LeftRailLayout/LeftRailContent/LeftRailContent';
import LeftRailLayout from '../../../components/LeftRailLayout/LeftRailLayout';
import TopCompany from '../../../components/Ratings/TopCompany/TopCompany';
import {
    SnowplowPageViewTracker,
    PAGE_VIEW_SECTIONS,
    PAGE_VIEW_TYPES,
} from '../../../components/Analytics/Snowplow/SnowplowPageViewTracker';

import {
    ATTRIBUTION_MODE_NONE,
    USER_RELATIONSHIP_ATTRIBUTION_NONE,
} from '../../../contexts/AbstractContext';
import { JobContextProvider } from '../../../contexts/JobContext';
import { Consumer as PageConsumer } from '../../../contexts/PageContext';
import './CompanyReviewsLandingPage.scss';

import { COMPANY_REVIEWS_SLIDES } from './Includes';

export default class CompanyReviewsLandingPage extends React.PureComponent {
    // Load Carousel
    //= =================================
    renderReviewCarousel = () => {
        const sliderProps = {
            dots: false,
            infinite: true,
            arrows: false,
            autoplay: true,
            autoplaySpeed: 5000,
            speed: 1000,
            slidesToShow: 1,
            slidesToScroll: 1,
            centerMode: false,
            adaptiveHeight: false,
            swipe: false,
        };

        const slides = [];
        // eslint-disable-next-line array-callback-return
        COMPANY_REVIEWS_SLIDES.map((slide) => {
            slides.push(
                <div key={slide.quote}>
                    <CompanyReviewSlide {...slide} />
                </div>
            );
        });

        return (
            <section className="review-slider-section">
                <FGBLabel>Company Reviews By Women, For Women</FGBLabel>
                <div className="dropshadow-div">
                    <NoSSR onSSR={slides[0]}>
                        <Slider settings={sliderProps}>{slides}</Slider>
                    </NoSSR>
                </div>
                <div className="button-div">{this.leaveReviewButton()}</div>
            </section>
        );
    };

    // Statistics Section
    //= =================================
    renderStatsSection = () => {
        return (
            <section className="company-reviews-landing-statistics-section">
                <FGBLabel className="title-label">
                    What the data tells us
                </FGBLabel>
                <div className="description-section">
                    The top priority of female job seekers when assessing a new
                    job opportunity is
                </div>
                <div className="keyword-section">Company Culture</div>
                <FGBLabel className="key-findings">Key Findings</FGBLabel>
                {this.renderPieCharts()}
                {this.renderBarChart()}
                {this.renderAverageRatingSection()}
                {this.renderPercentagePromoted()}
                {this.renderFlexibilityChart()}
            </section>
        );
    };

    // Pie Charts
    //= =================================
    renderPieCharts = () => {
        return (
            <div className="pie-chart-section">
                <MinimalCircularChart {...this.props.attitude} />
                <MinimalCircularChart {...this.props.ceoDiversity} />
            </div>
        );
    };

    // Bar Chart
    //= =================================
    renderBarChart = () => {
        return (
            <div className="bar-chart-section">
                <Barchart
                    data={this.props.salaryPercentages}
                    highestSectionLabel={this.props.topSalaryBracket}
                />
                <div className="number-display">
                    {this.props.topSalaryBracket}
                </div>
                <div className="description">
                    is the typical salary of our reviews
                </div>
            </div>
        );
    };

    // Wings Section
    //= =================================
    renderAverageRatingSection = () => {
        return (
            <div className="average-rating-section">
                <Rating
                    rating={this.props.averageJobSatisfaction}
                    size="large"
                    showNumber={false}
                />
                <div className="number-display">
                    {this.props.averageJobSatisfaction}
                </div>
                <div className="subheader">
                    is the average rating of companies based on thousands of
                    reviews
                </div>
            </div>
        );
    };

    // Percentage Promoted
    //= =================================
    renderPercentagePromoted = () => {
        return (
            <div className="promotion-equality-section">
                <div className="number-display">{`${this.props.promotionInequalityPercentage}%`}</div>
                <div className="section-description">
                    suggested they were not promoted equally to men
                </div>
            </div>
        );
    };

    // Flexibility
    //= =================================
    renderFlexibilityChart = () => {
        const flexibilityChartProps = {
            rating: this.props.averageFlexibility,
            totalRating: 5,
        };
        return (
            <div className="flexibility-chart-display">
                <div className="chart-section">
                    <div className="chart-label">Level of Flexibility</div>
                    <SliderChart {...flexibilityChartProps} />
                </div>
                <div className="flexibility-section-title">
                    Flexibility is key
                </div>
                <div className="flexibility-subheader">{`The average level of flexibility women reported at their employers is ${this.props.averageFlexibility}.`}</div>
            </div>
        );
    };

    // Best Companies For Women Section
    //= =================================
    renderTopCompaniesList = (pageProps) => {
        return (
            <section className="top-companies-list-section">
                <div className="top-companies-title">
                    Best Companies for Women
                </div>
                <JobContextProvider
                    content={[]}
                    session={pageProps.session}
                    attributionMode={ATTRIBUTION_MODE_NONE}
                    userRelationshipMode={USER_RELATIONSHIP_ATTRIBUTION_NONE}
                >
                    {this.props.topCompanies.map((company, idx) => {
                        const companyRefactoredProps = {
                            key: idx,
                            logo: company.logo,
                            companyId: company.companyId,
                            companyUrl: company.companyUrl,
                            companyName: `${company.position}. ${company.companyName}`,
                            isFollowed: company.isFollowed,
                            average: company.average,
                        };

                        return <TopCompany {...companyRefactoredProps} />;
                    })}
                </JobContextProvider>
                <div className="button-div">
                    <a href="/best-companies-for-women-2023">
                        <Button type="solid" value="See all" />
                    </a>
                </div>
            </section>
        );
    };

    // Footer
    //= =================================
    renderFooter = () => {
        return (
            <section className="footer-section">
                <div className="white-box">
                    <div className="title">Share an anonymous review</div>
                    <div className="subheader">
                        Pay it forward and help improve the workplace for women
                    </div>
                    <div className="button-div">{this.leaveReviewButton()}</div>
                </div>
            </section>
        );
    };

    leaveReviewButton = () => {
        return (
            <PageConsumer>
                {(context) =>
                    context.wrapAuth(
                        'review-button',
                        <Button
                            type="solid"
                            value="Leave a review"
                            onClick={
                                context.isLoggedIn()
                                    ? () => context.toggleJobReviewSurvey(true)
                                    : () => {}
                            }
                        />,
                        { login_trigger: 'login-to-review' }
                    )
                }
            </PageConsumer>
        );
    };

    render() {
        return (
            <div className="page-CompanyPages-CompanyReviewsLandingPage">
                <SnowplowPageViewTracker
                    pageType={PAGE_VIEW_TYPES.LANDING_PAGE}
                    pageSection={PAGE_VIEW_SECTIONS.COMPANY_REVIEWS}
                />
                <PageConsumer>
                    {(pageProps) => (
                        <Layout PAGE_PROPS={pageProps.getPageProps()}>
                            {this.renderReviewCarousel()}
                            <LeftRailLayout>
                                <LeftRailLayout.LeftRail>
                                    <LeftRailContent
                                        {...pageProps.pageProps.leftRail}
                                    />
                                </LeftRailLayout.LeftRail>
                                <LeftRailLayout.MainContent>
                                    {this.renderStatsSection()}
                                    {this.renderTopCompaniesList(
                                        pageProps.getPageProps()
                                    )}
                                </LeftRailLayout.MainContent>
                            </LeftRailLayout>
                            {this.renderFooter()}
                        </Layout>
                    )}
                </PageConsumer>
            </div>
        );
    }
}

CompanyReviewsLandingPage.propTypes = {
    /*
     * data for first pie charrt
     */
    attitude: PropTypes.shape({
        data: PropTypes.arrayOf(
            PropTypes.shape({
                title: PropTypes.string,
                value: PropTypes.number,
            })
        ),
        chartDescription: PropTypes.string,
    }),
    /*
     * data for second pie chart
     */
    ceoDiversity: PropTypes.shape({
        data: PropTypes.arrayOf(
            PropTypes.shape({
                title: PropTypes.string,
                value: PropTypes.number,
            })
        ),
        chartDescription: PropTypes.string,
    }),
    /*
     * Last Chart
     */
    promotionInequalityPercentage: PropTypes.number,
    /*
     * Wings
     */
    averageJobSatisfaction: PropTypes.string,
    /*
     * Slider
     */
    averageFlexibility: PropTypes.number,
    /*
     * Bar Graph
     */
    salaryPercentages: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            value: PropTypes.number,
        })
    ),
    /*
     * Which salary bracket is the highets (for bar chart)
     */
    topSalaryBracket: PropTypes.string,
    /*
     * Array of top companies
     */
    topCompanies: PropTypes.arrayOf(PropTypes.object),
};

CompanyReviewsLandingPage.defaultProps = {
    attitude: {},
    ceoDiversity: {},
    promotionInequalityPercentage: 0,
    averageFlexibility: 0,
    averageJobSatisfaction: '0.0',
    salaryPercentages: {},
    topSalaryBracket: '',
    topCompanies: {},
};
